/**
 * BSMS Reports Page
 * Displays 6 collapsible report sections. Each report has parameter inputs
 * (agent dropdown, date pickers, sort options). When the user clicks "Get Report",
 * parameters are sent to the backend which proxies to the BSMS Crystal Reports server
 * and returns a PDF displayed in a modal.
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  DocumentChartBarIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  getAvailableAgents,
  generateBsmsReport,
  BSMS_REPORTS,
  OPEN_LIABILITY_SORT_OPTIONS,
  UNEXECUTED_SORT_OPTIONS,
  FORFEITURE_SORT_OPTIONS,
  SORT_DIRECTIONS,
} from '../../../services/oathBsmsReportService';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathSelect,
  OathInput,
  OathModal,
  OathDualSpinner,
  OathEmptyState,
} from '../../shared/OathUI';
import { OathAnimatedPanel } from '../../shared/OathTableComponents';

// ============================================
// PDF VIEWER MODAL
// ============================================

const PdfViewerModal = ({ isOpen, onClose, pdfUrl, title }) => {
  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={title || 'Report Viewer'}
      size="xl"
    >
      <div className="w-full" style={{ height: '70vh' }}>
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Report PDF"
            className="w-full h-full border-0 rounded-lg"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            No report loaded
          </div>
        )}
      </div>
    </OathModal>
  );
};

// ============================================
// REPORT SECTION COMPONENT
// ============================================

const ReportSection = ({ title, isExpanded, onToggle, children }) => {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
      >
        <span className="font-semibold text-gray-900 dark:text-white">{title}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      <OathAnimatedPanel open={isExpanded}>
        <div className="px-5 py-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
          {children}
        </div>
      </OathAnimatedPanel>
    </div>
  );
};

// ============================================
// FORM FIELD WRAPPERS
// ============================================

const FormField = ({ label, children, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const FormRow = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

// ============================================
// INDIVIDUAL REPORT FORMS
// ============================================

/**
 * 1. Agency Vital Statistics Report Card
 */
const AgencyVitalStatisticsForm = ({ agents, onGenerate, loading }) => {
  const [agentId, setAgentId] = useState('');

  const handleSubmit = () => {
    if (!agentId) {
      toast.error('Please select an agent');
      return;
    }
    onGenerate({
      ...BSMS_REPORTS.AGENCY_VITAL_STATISTICS,
      additionalParams: { agentId },
    });
  };

  return (
    <>
      <FormField label="Agent" required>
        <OathSelect
          value={agentId}
          onChange={setAgentId}
          options={agents}
          placeholder="Select an agent..."
        />
      </FormField>
      <OathButton onClick={handleSubmit} loading={loading} disabled={loading}>
        Get Report
      </OathButton>
    </>
  );
};

/**
 * 2. Agent Statement Package
 */
const AgentStatementPackageForm = ({ agents, onGenerate, loading }) => {
  const [agentId, setAgentId] = useState('');
  const [showSubAgents, setShowSubAgents] = useState('N');

  const handleSubmit = () => {
    if (!agentId) {
      toast.error('Please select an agent');
      return;
    }
    onGenerate({
      ...BSMS_REPORTS.AGENT_STATEMENT_PACKAGE,
      additionalParams: { agentId, showSubAgents },
    });
  };

  return (
    <>
      <FormRow>
        <FormField label="Agent" required>
          <OathSelect
            value={agentId}
            onChange={setAgentId}
            options={agents}
            placeholder="Select an agent..."
          />
        </FormField>
        <FormField label="Show Sub Agents">
          <OathSelect
            value={showSubAgents}
            onChange={setShowSubAgents}
            options={[
              { value: 'Y', label: 'Yes' },
              { value: 'N', label: 'No' },
            ]}
          />
        </FormField>
      </FormRow>
      <OathButton onClick={handleSubmit} loading={loading} disabled={loading}>
        Get Report
      </OathButton>
    </>
  );
};

/**
 * 3. Agent Open Liability Details
 */
const AgentOpenLiabilityForm = ({ agents, onGenerate, loading }) => {
  const [agentId, setAgentId] = useState('');
  const [minLiability, setMinLiability] = useState('');
  const [sortBy, setSortBy] = useState('Type');
  const [sortDir, setSortDir] = useState('ASC');

  const handleSubmit = () => {
    if (!agentId) {
      toast.error('Please select an agent');
      return;
    }
    onGenerate({
      ...BSMS_REPORTS.AGENT_OPEN_LIABILITY,
      additionalParams: {
        agentId,
        minLiability: minLiability || '0',
        sortBy,
        sortDirection: sortDir,
      },
    });
  };

  return (
    <>
      <FormRow>
        <FormField label="Agent" required>
          <OathSelect
            value={agentId}
            onChange={setAgentId}
            options={agents}
            placeholder="Select an agent..."
          />
        </FormField>
        <FormField label="Minimum Liability">
          <OathInput
            type="number"
            value={minLiability}
            onChange={setMinLiability}
            placeholder="0"
            prefix="$"
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Sort By">
          <OathSelect
            value={sortBy}
            onChange={setSortBy}
            options={OPEN_LIABILITY_SORT_OPTIONS}
          />
        </FormField>
        <FormField label="Sort Direction">
          <OathSelect
            value={sortDir}
            onChange={setSortDir}
            options={SORT_DIRECTIONS}
          />
        </FormField>
      </FormRow>
      <OathButton onClick={handleSubmit} loading={loading} disabled={loading}>
        Get Report
      </OathButton>
    </>
  );
};

/**
 * 4. Agent Unexecuted Detail
 */
const AgentUnexecutedDetailForm = ({ agents, onGenerate, loading }) => {
  const [agentId, setAgentId] = useState('');
  const [sortBy, setSortBy] = useState('PowerNo');
  const [sortDir, setSortDir] = useState('ASC');

  const handleSubmit = () => {
    if (!agentId) {
      toast.error('Please select an agent');
      return;
    }
    onGenerate({
      ...BSMS_REPORTS.AGENT_UNEXECUTED_DETAIL,
      additionalParams: { agentId, sortBy, sortDirection: sortDir },
    });
  };

  return (
    <>
      <FormField label="Agent" required>
        <OathSelect
          value={agentId}
          onChange={setAgentId}
          options={agents}
          placeholder="Select an agent..."
        />
      </FormField>
      <FormRow>
        <FormField label="Sort By">
          <OathSelect
            value={sortBy}
            onChange={setSortBy}
            options={UNEXECUTED_SORT_OPTIONS}
          />
        </FormField>
        <FormField label="Sort Direction">
          <OathSelect
            value={sortDir}
            onChange={setSortDir}
            options={SORT_DIRECTIONS}
          />
        </FormField>
      </FormRow>
      <OathButton onClick={handleSubmit} loading={loading} disabled={loading}>
        Get Report
      </OathButton>
    </>
  );
};

/**
 * 5. Expired Unexecuted Detail by Agent
 */
const ExpiredUnexecutedDetailForm = ({ agents, onGenerate, loading }) => {
  const [agentId, setAgentId] = useState('');
  const [expirationDate, setExpirationDate] = useState('');

  const handleSubmit = () => {
    if (!agentId) {
      toast.error('Please select an agent');
      return;
    }
    if (!expirationDate) {
      toast.error('Please select an expiration date');
      return;
    }
    onGenerate({
      ...BSMS_REPORTS.EXPIRED_UNEXECUTED_DETAIL,
      additionalParams: { agentId, expirationDate },
    });
  };

  return (
    <>
      <FormRow>
        <FormField label="Agent" required>
          <OathSelect
            value={agentId}
            onChange={setAgentId}
            options={agents}
            placeholder="Select an agent..."
          />
        </FormField>
        <FormField label="Expiration Date" required>
          <OathInput
            type="date"
            value={expirationDate}
            onChange={setExpirationDate}
          />
        </FormField>
      </FormRow>
      <OathButton onClick={handleSubmit} loading={loading} disabled={loading}>
        Get Report
      </OathButton>
    </>
  );
};

/**
 * 6. Forfeiture Reports
 */
const ForfeitureReportsForm = ({ agents, onGenerate, loading }) => {
  const [agentId, setAgentId] = useState('');
  const [showSubAgents, setShowSubAgents] = useState('N');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupByExecutingAgent, setGroupByExecutingAgent] = useState('N');
  const [sortBy, setSortBy] = useState('FJDue');
  const [sortDir, setSortDir] = useState('ASC');

  const handleSubmit = () => {
    if (!agentId) {
      toast.error('Please select an agent');
      return;
    }

    onGenerate({
      ...BSMS_REPORTS.FORFEITURE,
      additionalParams: {
        agentId,
        showSubAgents,
        startDate,
        endDate,
        groupByExecutingAgent,
        sortBy,
        sortDirection: sortDir,
      },
    });
  };

  return (
    <>
      <FormField label="Agent" required>
        <OathSelect
          value={agentId}
          onChange={setAgentId}
          options={agents}
          placeholder="Select an agent..."
        />
      </FormField>

      <FormRow>
        <FormField label="Show Sub Agents">
          <OathSelect
            value={showSubAgents}
            onChange={setShowSubAgents}
            options={[
              { value: 'Y', label: 'Yes' },
              { value: 'N', label: 'No' },
            ]}
          />
        </FormField>
        <FormField label="Group by Executing Agent">
          <OathSelect
            value={groupByExecutingAgent}
            onChange={setGroupByExecutingAgent}
            options={[
              { value: 'Y', label: 'Yes' },
              { value: 'N', label: 'No' },
            ]}
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Start Date">
          <OathInput
            type="date"
            value={startDate}
            onChange={setStartDate}
          />
        </FormField>
        <FormField label="End Date">
          <OathInput
            type="date"
            value={endDate}
            onChange={setEndDate}
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Sort By">
          <OathSelect
            value={sortBy}
            onChange={setSortBy}
            options={FORFEITURE_SORT_OPTIONS}
          />
        </FormField>
        <FormField label="Sort Direction">
          <OathSelect
            value={sortDir}
            onChange={setSortDir}
            options={SORT_DIRECTIONS}
          />
        </FormField>
      </FormRow>

      <OathButton onClick={handleSubmit} loading={loading} disabled={loading}>
        Get Report
      </OathButton>
    </>
  );
};

// ============================================
// MAIN PAGE COMPONENT
// ============================================

const BsmsReportsPage = () => {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, title: '' });

  // Show all reports — backend validates agent access per request.
  const isGeneralAgent = true;

  // Load agents on first section expand
  const loadAgents = useCallback(async () => {
    if (agentsLoaded || agentsLoading) return;
    setAgentsLoading(true);
    try {
      const result = await getAvailableAgents();
      if (result.success) {
        // Transform to OathSelect options format
        const agentOptions = Array.isArray(result.data)
          ? result.data.map((a) => {
              const id = String(a.key || a.agentId || a.id);
              const name = a.value || a.name || a.agentName || `Agent ${id}`;
              return { value: id, label: `${id} - ${name}` };
            })
          : Object.entries(result.data || {}).map(([name, id]) => ({
              value: String(id),
              label: `${id} - ${name}`,
            }));
        setAgents(agentOptions);
      } else {
        toast.error(result.error || 'Failed to load agents');
      }
    } finally {
      setAgentsLoading(false);
      setAgentsLoaded(true);
    }
  }, [agentsLoaded, agentsLoading]);

  // Handle section toggle
  const handleToggle = (sectionId) => {
    const isOpening = expandedSection !== sectionId;
    setExpandedSection(isOpening ? sectionId : null);
    if (isOpening && !agentsLoaded) {
      loadAgents();
    }
  };

  // Handle report generation
  const handleGenerateReport = async ({ reportName, paramSet, additionalParams, title }) => {
    setGenerating(true);
    try {
      const result = await generateBsmsReport({
        reportName,
        paramSet,
        additionalParams,
      });

      if (result.success && result.data) {
        // Revoke previous blob URL to prevent memory leak
        if (pdfModal.url) {
          URL.revokeObjectURL(pdfModal.url);
        }
        const url = URL.createObjectURL(result.data);
        setPdfModal({ open: true, url, title: title || reportName });
      } else {
        toast.error(result.error || 'Failed to generate report');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Cleanup PDF blob URL on modal close
  const handleCloseModal = () => {
    if (pdfModal.url) {
      URL.revokeObjectURL(pdfModal.url);
    }
    setPdfModal({ open: false, url: null, title: '' });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfModal.url) {
        URL.revokeObjectURL(pdfModal.url);
      }
    };
  }, [pdfModal.url]);

  const reportSections = [
    {
      id: BSMS_REPORTS.AGENCY_VITAL_STATISTICS.id,
      title: BSMS_REPORTS.AGENCY_VITAL_STATISTICS.title,
      show: isGeneralAgent,
      form: <AgencyVitalStatisticsForm agents={agents} onGenerate={handleGenerateReport} loading={generating} />,
    },
    {
      id: BSMS_REPORTS.AGENT_STATEMENT_PACKAGE.id,
      title: BSMS_REPORTS.AGENT_STATEMENT_PACKAGE.title,
      show: isGeneralAgent,
      form: <AgentStatementPackageForm agents={agents} onGenerate={handleGenerateReport} loading={generating} />,
    },
    {
      id: BSMS_REPORTS.AGENT_OPEN_LIABILITY.id,
      title: BSMS_REPORTS.AGENT_OPEN_LIABILITY.title,
      show: true,
      form: <AgentOpenLiabilityForm agents={agents} onGenerate={handleGenerateReport} loading={generating} />,
    },
    {
      id: BSMS_REPORTS.AGENT_UNEXECUTED_DETAIL.id,
      title: BSMS_REPORTS.AGENT_UNEXECUTED_DETAIL.title,
      show: true,
      form: <AgentUnexecutedDetailForm agents={agents} onGenerate={handleGenerateReport} loading={generating} />,
    },
    {
      id: BSMS_REPORTS.EXPIRED_UNEXECUTED_DETAIL.id,
      title: BSMS_REPORTS.EXPIRED_UNEXECUTED_DETAIL.title,
      show: true,
      form: <ExpiredUnexecutedDetailForm agents={agents} onGenerate={handleGenerateReport} loading={generating} />,
    },
    {
      id: BSMS_REPORTS.FORFEITURE.id,
      title: BSMS_REPORTS.FORFEITURE.title,
      show: true,
      form: <ForfeitureReportsForm agents={agents} onGenerate={handleGenerateReport} loading={generating} />,
    },
  ];

  const visibleSections = reportSections.filter((s) => s.show);

  return (
    <OathPageWrapper>
      <OathHeaderCard
        title="Bankers Reports"
        subtitle="Generate and view Reports"
        icon={DocumentChartBarIcon}
      />

      <OathContentCard>
        {agentsLoading && !agentsLoaded ? (
          <div className="flex justify-center py-8">
            <OathDualSpinner size="lg" />
          </div>
        ) : visibleSections.length === 0 ? (
          <OathEmptyState
            icon={DocumentChartBarIcon}
            title="No Reports Available"
            message="You do not have access to any reports."
          />
        ) : (
          <div className="space-y-3">
            {visibleSections.map((section) => (
              <ReportSection
                key={section.id}
                title={section.title}
                isExpanded={expandedSection === section.id}
                onToggle={() => handleToggle(section.id)}
              >
                {section.form}
              </ReportSection>
            ))}
          </div>
        )}
      </OathContentCard>

      <PdfViewerModal
        isOpen={pdfModal.open}
        onClose={handleCloseModal}
        pdfUrl={pdfModal.url}
        title={pdfModal.title}
      />
    </OathPageWrapper>
  );
};

export default BsmsReportsPage;
